import { Button, Radio, Space, Typography } from "antd";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api from "../api/axios.js";
import { useChatbot } from "../contexts/ChatContext.jsx";
import { useEventAction } from "../contexts/EventContext.jsx";
import { useView } from "../contexts/ViewContext.jsx";

import ActivityCard from "./ActivityCard.jsx";
import styles from "./PFQuiz.module.css";

const { Title } = Typography;

function PFQuiz({ learningJourneyId, view }) {
    const data = view.view_data;
    const { setMessages, setShowChatbot, setOpen, streamRequest } = useChatbot();
    const { setOnAdvance } = useView();

    const [selectedId, setSelectedId] = useState(data.selected_option_id ?? null);
    const [submittedId, setSubmittedId] = useState(data.selected_option_id ?? null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setSelectedId(data.selected_option_id ?? null);
        setSubmittedId(data.selected_option_id ?? null);
        setSubmitting(false);
        setOnAdvance(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view.view_id]);

    // Unregister on unmount so a later, unrelated view's "Continue" click can never
    // fire this quiz's completion call.
    useEffect(() => () => setOnAdvance(null), [setOnAdvance]);

    const armAdvance = () => {
        const viewId = view.view_id;
        setOnAdvance(async () => {
            try {
                await api.post(`/workspace/pf-quiz/mark-complete/`, {
                    learning_journey_id: learningJourneyId,
                    view_id: viewId,
                });
            } catch (error) {
                console.error("Failed to mark PF quiz complete:", error);
            }
        });
    };

    // Fires both on a reload after the tutor already closed the session (view_schema()
    // re-emits its "next-step" control on fetch) and live, mid-stream, the moment
    // close_session actually runs -- one listener covers both cases.
    useEventAction("next-step", (event) => {
        if (event.predicate === "next-view") armAdvance();
    });

    const submit = () => {
        if (!selectedId || submittedId || submitting) return;
        setSubmittedId(selectedId);
        setSubmitting(true);

        // "chat opens once there's something worth discussing": the popover only
        // becomes visible once the opening turn is actually in flight, streamed
        // straight into it -- not as an empty affordance appearing on its own.
        setMessages([{ role: "assistant", blocks: [] }]);
        setShowChatbot(true);
        setOpen(true);
        streamRequest("/workspace/pf-quiz/submit/", {
            learning_journey_id: learningJourneyId,
            option_id: selectedId,
        }).finally(() => setSubmitting(false));
    };

    return (
        <ActivityCard view={view}>
            <Title level={4}>{data.question}</Title>

            {data.body && (
                <div className={styles.scenario}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{data.body}</ReactMarkdown>
                </div>
            )}

            <Radio.Group
                value={selectedId}
                disabled={submitting || submittedId != null}
                onChange={(e) => setSelectedId(e.target.value)}
                style={{ width: "100%", marginBottom: "20px" }}
            >
                <Space direction="vertical" style={{ width: "100%" }}>
                    {data.options.map((option) => {
                        const isGrayedOut = submittedId != null && option.id !== submittedId;
                        return (
                            <div key={option.id} className={styles.optionRow}>
                                <Radio
                                    value={option.id}
                                    size="large"
                                    className={isGrayedOut ? styles.optionGrayed : ""}
                                >
                                    {option.text}
                                </Radio>
                            </div>
                        );
                    })}
                </Space>
            </Radio.Group>

            <Button
                className={styles.submitButton}
                onClick={submit}
                disabled={!selectedId || submitting || submittedId != null}
            >
                Lock in my guess
            </Button>
        </ActivityCard>
    );
}

export default PFQuiz;
